import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import EventWorks from '../components/EventWorks'
import EventPrivateSubmit from '../components/EventPrivateSubmit'
import { renderMarkdown } from '../lib/markdown'
import { eventsApi, type EventDetail } from '../services/api'
import { formatEventRange } from '../lib/datetime'
import '../styles/blog.css'
import '../styles/events.css'

type SocialIconName = 'x' | 'instagram' | 'github' | 'linkedin'

const SOCIAL_LINKS: Array<{ label: string; href: string; icon: SocialIconName }> = [
  { label: 'X / Twitter', href: 'https://x.com/opendeploy', icon: 'x' },
  { label: 'Instagram', href: 'https://www.instagram.com/opendeploy/', icon: 'instagram' },
  { label: 'GitHub', href: 'https://github.com/opendeploy-dev', icon: 'github' },
  { label: 'LinkedIn', href: 'https://linkedin.com/company/opendeploy/', icon: 'linkedin' },
]

// Build a Google Calendar "add event" link from the event's ISO times.
function googleCalendarUrl(event: EventDetail): string | null {
  if (!event.starts_at) return null
  const start = new Date(event.starts_at)
  if (Number.isNaN(start.getTime())) return null
  const end =
    event.ends_at && !Number.isNaN(new Date(event.ends_at).getTime())
      ? new Date(event.ends_at)
      : new Date(start.getTime() + 60 * 60 * 1000)
  const stamp = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.name,
    dates: `${stamp(start)}/${stamp(end)}`,
  })
  if (event.description) params.set('details', event.description)
  if (event.location) params.set('location', event.location)
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M7 3v3M17 3v3M4 8h16M5 6h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11Z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.5" fill="none" stroke="currentColor" strokeWidth={1.7} />
    </svg>
  )
}

function DiscordIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286ZM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189Zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  )
}

function MeetIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M5.53 2.13 0 7.75h5.53zm.398 0v5.62h7.608v3.65l5.47-4.45c-.014-1.22.031-2.25-.025-3.46-.148-1.09-1.287-1.47-2.236-1.36zM23.1 4.32c-.802.295-1.358.995-2.047 1.49-2.506 2.05-4.982 4.12-7.468 6.19 3.025 2.59 6.04 5.18 9.065 7.76 1.218.671 1.428-.814 1.328-1.64v-13a.828.828 0 0 0-.877-.825zM.038 8.15v7.7h5.53v-7.7zm13.577 8.1H6.008v5.62c3.864-.006 7.737.011 11.58-.009 1.02-.07 1.618-1.12 1.468-2.07v-2.51l-5.47-4.68v3.65zm-13.577 0c.02 1.44-.041 2.88.033 4.31.162.948 1.158 1.43 2.047 1.31h3.464v-5.62z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  )
}

function WechatIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.804-.06-.169-.583-.262-1.193-.262-1.83 0-3.831 3.708-6.937 8.282-6.937.291 0 .581.018.867.04C18.81 4.764 14.13 2.188 8.691 2.188zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-3.93 0-7.11 2.652-7.11 5.925 0 3.273 3.18 5.925 7.11 5.925.834 0 1.633-.12 2.378-.336a.722.722 0 0 1 .598.082l1.583.926a.272.272 0 0 0 .14.045.24.24 0 0 0 .24-.24c0-.06-.024-.12-.04-.18l-.325-1.231a.49.49 0 0 1 .177-.554C22.99 18.04 24 16.62 24 14.783c0-3.273-3.18-5.925-7.062-5.925zM14.13 12.49a.97.97 0 1 1 0-1.94.97.97 0 0 1 0 1.94zm4.838 0a.97.97 0 1 1 0-1.94.97.97 0 0 1 0 1.94z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  )
}

function SocialIcon({ name }: { name: SocialIconName }) {
  if (name === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077"
          fill="currentColor"
          stroke="none"
        />
      </svg>
    )
  }

  if (name === 'github') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"
          fill="currentColor"
          stroke="none"
        />
      </svg>
    )
  }

  if (name === 'linkedin') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286ZM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065Zm1.782 13.019H3.555V9h3.564v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003Z"
          fill="currentColor"
          stroke="none"
        />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  )
}

function EventCommunityCard({
  meetUrl,
  discordUrl,
  wechatQrUrl,
}: {
  meetUrl?: string
  discordUrl?: string
  wechatQrUrl?: string
}) {
  // The WeChat group can't be joined by a link (group QRs rotate), so the button
  // opens a popup with the admin-uploaded QR for the visitor to scan.
  const [showWechat, setShowWechat] = useState(false)

  const hasRoom = !!meetUrl
  const hasChat = !!discordUrl || !!wechatQrUrl

  return (
    <aside className="od-event-community-card" aria-label="Event community links">
      <span className="od-event-community-kicker">Community</span>

      {hasRoom && (
        <div className="od-event-community-block">
          <h2>Join the room</h2>
          <div className="od-event-community-actions">
            <a href={meetUrl} target="_blank" rel="noopener noreferrer">
              <MeetIcon />
              <span>Join Google Meeting</span>
            </a>
          </div>
        </div>
      )}

      {hasChat && (
        <div className="od-event-community-block">
          <h2>Join chat group</h2>
          <div className="od-event-community-actions">
            {discordUrl && (
              <a href={discordUrl} target="_blank" rel="noopener noreferrer">
                <DiscordIcon />
                <span>Join Discord</span>
              </a>
            )}
            {wechatQrUrl && (
              <button type="button" onClick={() => setShowWechat(true)}>
                <WechatIcon />
                <span>Join WeChat</span>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="od-event-community-social">
        <h3>Follow us on social media</h3>
        <div className="od-event-community-icons">
          {SOCIAL_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={link.label}
              title={link.label}
            >
              <SocialIcon name={link.icon} />
            </a>
          ))}
        </div>
      </div>

      {showWechat && wechatQrUrl && (
        <div
          className="od-event-wechat-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Join our WeChat group"
          onClick={() => setShowWechat(false)}
        >
          <div className="od-event-wechat-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="od-event-wechat-close"
              onClick={() => setShowWechat(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3>Join our WeChat group</h3>
            <img src={wechatQrUrl} alt="WeChat group QR code" />
            <p>Scan with WeChat to join the group</p>
          </div>
        </div>
      )}
    </aside>
  )
}

type HeroDisplay = {
  eyebrow: string
  title: string
  dateLabel: string
  timeLabel: string
  locationName: string
  ctaLabel: string
}

function formatHeroLabels(event: EventDetail) {
  const range = formatEventRange(event.starts_at, event.ends_at, event.timezone, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const [dateLabel = '', timeLabel = ''] = range.split(' · ')
  return { dateLabel, timeLabel }
}

function getEventHero(event: EventDetail): HeroDisplay {
  const now = Date.now()
  const endTime = event.ends_at ? new Date(event.ends_at).getTime() : NaN
  const startTime = event.starts_at ? new Date(event.starts_at).getTime() : NaN
  const isPast = Number.isFinite(endTime)
    ? endTime < now
    : Number.isFinite(startTime) && startTime < now
  const labels = formatHeroLabels(event)
  return {
    eyebrow: isPast ? 'Past event' : 'Upcoming event',
    title: event.name,
    dateLabel: labels.dateLabel,
    timeLabel: labels.timeLabel,
    locationName: event.location || 'Online',
    ctaLabel: isPast ? 'View recap' : 'View details',
  }
}

export default function EventDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [event, setEvent] = useState<EventDetail | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'notfound'>('loading')

  useEffect(() => {
    let active = true
    setStatus('loading')
    eventsApi
      .getEvent(slug)
      .then((res) => {
        if (!active) return
        setEvent(res.data)
        setStatus('ready')
      })
      .catch(() => {
        if (active) setStatus('notfound')
      })
    return () => {
      active = false
    }
  }, [slug])

  useEffect(() => {
    if (status === 'notfound') {
      const t = setTimeout(() => navigate('/events', { replace: true }), 2200)
      return () => clearTimeout(t)
    }
  }, [status, navigate])

  const agenda = useMemo(
    () => (event?.agenda_markdown ? renderMarkdown(event.agenda_markdown) : { html: '', toc: [] }),
    [event],
  )

  const description = useMemo(
    () => (event?.description ? renderMarkdown(event.description) : { html: '', toc: [] }),
    [event],
  )

  const calendarUrl = useMemo(() => (event ? googleCalendarUrl(event) : null), [event])
  const hero = useMemo(() => (event ? getEventHero(event) : null), [event])

  if (status === 'loading') {
    return (
      <div className="od-event-page od-blog-light od-event-light">
        <SiteNav />
        <div className="od-event-detail-frame">
          <p className="od-event-state">Loading…</p>
        </div>
        <Footer />
      </div>
    )
  }

  if (status === 'notfound' || !event) {
    return (
      <div className="od-event-page od-blog-light od-event-light">
        <SiteNav />
        <div className="od-event-detail-frame">
          <p className="od-event-state">
            Event not found — taking you back to <Link to="/events">events</Link>.
          </p>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <>
      <SEO
        title={`${hero?.title || event.name} — OpenDeploy Events`}
        description={event.description}
        path={`/events/${event.slug}`}
      />

      <div className="od-event-page od-blog-light od-event-light od-event-detail-page">
        <SiteNav />
        <main className="od-event-detail">
          {hero && (
            <header className={`od-event-detail-hero${event.cover_image_url ? '' : ' is-plain'}`}>
              {event.cover_image_url && (
                <img
                  className="od-event-detail-hero-img"
                  src={event.cover_image_url}
                  alt=""
                  loading="eager"
                />
              )}
              <div className="od-event-detail-hero-glass" aria-hidden="true" />
              <div className="od-event-detail-hero-content">
                <div className="od-event-detail-hero-text">
                  <span className="od-event-detail-kicker">{hero.eyebrow}</span>
                  <h1>{hero.title}</h1>
                  <div className="od-event-detail-hero-meta">
                    {(hero.dateLabel || hero.timeLabel) && (
                      <div className="od-event-detail-hero-row">
                        <span className="od-event-detail-hero-icon">
                          <CalendarIcon />
                        </span>
                        <span className="od-event-detail-hero-copy">
                          {hero.dateLabel && <strong>{hero.dateLabel}</strong>}
                          {hero.timeLabel && <span>{hero.timeLabel}</span>}
                        </span>
                      </div>
                    )}
                    {hero.locationName && (
                      <div className="od-event-detail-hero-row">
                        <span className="od-event-detail-hero-icon">
                          <PinIcon />
                        </span>
                        <span className="od-event-detail-hero-copy">
                          <strong>{hero.locationName}</strong>
                        </span>
                      </div>
                    )}
                  </div>
                  <a className="od-event-detail-hero-cta" href="#event-recap">
                    {hero.ctaLabel}
                  </a>
                </div>
                <EventCommunityCard
                  meetUrl={event.meet_url}
                  discordUrl={event.discord_url}
                  wechatQrUrl={event.wechat_qr_url}
                />
              </div>
            </header>
          )}

          <div className="od-event-detail-frame">
            <nav className="od-event-breadcrumb" aria-label="Breadcrumb">
              <Link to="/events">Events</Link>
              <span aria-hidden="true">/</span>
              <span>{hero?.title || event.name}</span>
            </nav>

            <div className="od-event-detail-main">
              <div className="od-event-detail-content">
                <section className="od-event-detail-intro" aria-label="Event details">
                  {description.html && (
                    <div
                      className="od-event-detail-lede"
                      dangerouslySetInnerHTML={{ __html: description.html }}
                    />
                  )}
                  {calendarUrl && (
                    <a
                      className="od-event-detail-cal"
                      href={calendarUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <CalendarIcon />
                      Add to calendar
                    </a>
                  )}
                </section>

                {agenda.html && (
                  <section className="od-event-agenda" aria-label="Agenda">
                    <h2 className="od-event-agenda-title">Agenda</h2>
                    <div className="od-event-agenda-body" dangerouslySetInnerHTML={{ __html: agenda.html }} />
                  </section>
                )}

                <div id="event-recap">
                  <EventWorks slug={event.slug} submissionsEnabled={event.submissions_enabled} />
                </div>

                {event.private_submissions_enabled && <EventPrivateSubmit slug={event.slug} />}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  )
}
